/**
 * Collection Carousel
 * GSAP-powered animations with staggered entrance and image cycling
 */

document.addEventListener('DOMContentLoaded', () => {
  const carousels = document.querySelectorAll('[data-collection-carousel]');
  
  carousels.forEach(carousel => {
    initCollectionCarousel(carousel);
  });
});

function initCollectionCarousel(carousel) {
  const items = carousel.querySelectorAll('[data-carousel-item]');
  const collectionList = carousel.querySelector('[data-collection-list]');
  
  if (!items.length) return;
  
  // Initialize Phosphor icons
  if (window.UntamedIcons && typeof window.UntamedIcons.refresh === 'function') {
    window.UntamedIcons.refresh();
  }
  
  // GSAP Entrance Animation
  if (typeof gsap !== 'undefined' && typeof ScrollTrigger !== 'undefined') {
    gsap.registerPlugin(ScrollTrigger);
    
    // Set initial state
    gsap.set(items, {
      opacity: 0,
      y: 30
    });
    
    // Create scroll-triggered animation
    gsap.to(items, {
      opacity: 1,
      y: 0,
      duration: 0.6,
      ease: 'power3.out',
      stagger: {
        amount: 0.5,
        from: 'start'
      },
      scrollTrigger: {
        trigger: carousel,
        start: 'top 85%',
        once: true
      },
      onComplete: () => {
        carousel.classList.add('is-animated');
        // Initialize image cycling after entrance animation
        initImageCycling(collectionList);
        // Initialize title marquees
        initTitleMarquees(collectionList);
      }
    });
  } else {
    // Fallback: show items immediately if GSAP not available
    carousel.classList.add('is-animated');
    items.forEach(item => {
      item.style.opacity = '1';
      item.style.transform = 'translateY(0)';
    });
    initImageCycling(collectionList);
    initTitleMarquees(collectionList);
  }
  
  // Drag to scroll functionality
  initDragScroll(carousel.querySelector('[data-carousel]'));
}

/**
 * Initialize image cycling for collection items
 */
function initImageCycling(collectionList) {
  if (!collectionList) return;
  
  const cycleEnabled = collectionList.dataset.cycleImages === 'true';
  if (!cycleEnabled) return;
  
  const cycleInterval = parseFloat(collectionList.dataset.cycleInterval || 3) * 1000;
  const cycleableItems = collectionList.querySelectorAll('[data-cycle-images]');
  
  cycleableItems.forEach(item => {
    initItemImageCycle(item, cycleInterval);
  });
}

/**
 * Initialize image cycling for a single collection item
 */
function initItemImageCycle(item, interval) {
  const imageUrls = item.dataset.cycleImages.split(',');
  if (imageUrls.length < 2) return;
  
  // Support both class naming conventions
  const imageStack = item.querySelector('.collection-list__image-stack, .collection-list-container__image-stack');
  if (!imageStack) return;
  
  // Detect which class convention is used
  const isContainer = imageStack.classList.contains('collection-list-container__image-stack');
  const imageClass = isContainer ? 'collection-list-container__image' : 'collection-list__image';
  const activeClass = imageClass + '--active';
  
  // Get or create images
  let currentIndex = 0;
  
  // Preload and create image elements for cycling
  const imageElements = [];
  
  // First image is already in the DOM
  const existingImage = imageStack.querySelector('.' + imageClass);
  if (existingImage) {
    imageElements.push(existingImage);
  }
  
  // Create additional images
  imageUrls.forEach((url, index) => {
    if (index === 0 && existingImage) {
      // Update first image src if needed
      existingImage.src = url;
      return;
    }
    
    const img = document.createElement('img');
    img.src = url;
    img.alt = '';
    img.className = imageClass;
    img.dataset.imageIndex = index;
    img.loading = 'lazy';
    imageStack.appendChild(img);
    imageElements.push(img);
  });
  
  // Cycling animation with GSAP
  let cycleTimer = null;
  
  function cycleToNext() {
    // Skip animation if GSAP not loaded (cycling still works, just no animation)
    if (typeof gsap === 'undefined') {
      // Simple fallback: just swap classes without animation
      const currentImage = imageElements[currentIndex];
      const nextIndex = (currentIndex + 1) % imageElements.length;
      const nextImage = imageElements[nextIndex];
      if (currentImage) currentImage.classList.remove(activeClass);
      if (nextImage) nextImage.classList.add(activeClass);
      currentIndex = nextIndex;
      return;
    }
    
    const currentImage = imageElements[currentIndex];
    const nextIndex = (currentIndex + 1) % imageElements.length;
    const nextImage = imageElements[nextIndex];
    
    if (!currentImage || !nextImage) return;
    
    // Crossfade animation
    gsap.to(currentImage, {
      opacity: 0,
      scale: 1.05,
      duration: 0.5,
      ease: 'power2.inOut',
      onComplete: () => {
        currentImage.classList.remove(activeClass);
        gsap.set(currentImage, { scale: 1 });
      }
    });
    
    gsap.fromTo(nextImage,
      { opacity: 0, scale: 0.95 },
      {
        opacity: 1,
        scale: 1,
        duration: 0.5,
        ease: 'power2.inOut',
        onComplete: () => {
          nextImage.classList.add(activeClass);
        }
      }
    );
    
    currentIndex = nextIndex;
  }
  
  function startCycling() {
    if (cycleTimer) return;
    cycleTimer = setInterval(cycleToNext, interval);
  }
  
  function stopCycling() {
    if (cycleTimer) {
      clearInterval(cycleTimer);
      cycleTimer = null;
    }
  }
  
  // Start cycling when visible
  if (typeof IntersectionObserver !== 'undefined') {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          startCycling();
        } else {
          stopCycling();
        }
      });
    }, { threshold: 0.3 });
    
    observer.observe(item);
  } else {
    // Fallback: just start cycling
    startCycling();
  }
  
  // Pause on hover
  item.addEventListener('mouseenter', stopCycling);
  item.addEventListener('mouseleave', startCycling);
}

/**
 * Initialize title marquees for overflowing titles only
 */
function initTitleMarquees(collectionList) {
  if (!collectionList) return;
  
  const marqueeEnabled = collectionList.dataset.enableMarquee === 'true';
  if (!marqueeEnabled) return;
  
  const titleWrappers = collectionList.querySelectorAll('[data-title-wrapper]');
  
  titleWrappers.forEach(wrapper => {
    const titleText = wrapper.querySelector('.collection-list__title-text, .collection-list-container__title-text');
    if (!titleText) return;
    
    // Use requestAnimationFrame to ensure layout is complete
    requestAnimationFrame(() => {
      // Get the wrapper's max-width (which is the image size)
      const wrapperStyle = getComputedStyle(wrapper);
      const maxWidth = parseFloat(wrapperStyle.maxWidth);
      
      // If no max-width set, use wrapper's actual width
      const containerWidth = maxWidth > 0 ? maxWidth : wrapper.offsetWidth;
      
      // Measure text by creating a temporary measuring element
      const measurer = document.createElement('span');
      measurer.style.cssText = 'position:absolute;visibility:hidden;white-space:nowrap;font:inherit;';
      measurer.textContent = titleText.textContent;
      wrapper.appendChild(measurer);
      const textWidth = measurer.offsetWidth;
      wrapper.removeChild(measurer);
      
      // Only enable marquee if text actually overflows
      if (textWidth > containerWidth) {
        wrapper.classList.add('is-marquee');
      }
    });
  });
}

/**
 * Drag to scroll functionality
 */
function initDragScroll(carousel) {
  if (!carousel) return;
  
  let isDown = false;
  let startX;
  let scrollLeft;
  
  carousel.addEventListener('mousedown', (e) => {
    // Don't start drag on links
    if (e.target.closest('a')) return;
    
    isDown = true;
    carousel.style.cursor = 'grabbing';
    startX = e.pageX - carousel.offsetLeft;
    scrollLeft = carousel.scrollLeft;
  });
  
  carousel.addEventListener('mouseleave', () => {
    isDown = false;
    carousel.style.cursor = '';
  });
  
  carousel.addEventListener('mouseup', () => {
    isDown = false;
    carousel.style.cursor = '';
  });
  
  carousel.addEventListener('mousemove', (e) => {
    if (!isDown) return;
    e.preventDefault();
    const x = e.pageX - carousel.offsetLeft;
    const walk = (x - startX) * 1.5; // Scroll speed multiplier
    carousel.scrollLeft = scrollLeft - walk;
  });
}
